// Xavier (successor to Scholar, recycled per DIR-084 — Informed Consent)
import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Loader2, Volume2, VolumeX } from 'lucide-react';
import useSpeechSynthesis from '../hooks/useSpeechSynthesis';
import useSpeechRecognition from '../hooks/useSpeechRecognition';
import useAutoSpeakPreference from '../hooks/useAutoSpeakPreference';
import SpeakButton from './SpeakButton';
import MicButton from './MicButton';
import { getPersona } from '../data/federationPersonas';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AI Chat Popup — persona-flexible chat window.
 *
 * Architect directive: triggered by FederationFaces tiles. The selected
 * persona determines the avatar, accent color, header label, greeting,
 * and the `persona` flag we pass to the backend `/api/chat/` endpoint.
 *
 * For non-Xavier personas, Xavier's backend chat endpoint is a polite
 * fallback — it responds in-character to questions but reminds the user
 * which sibling node owns the conversation. Once each Federation node's
 * own chat endpoint is exposed, this popup will route by persona.
 */
export default function AIChatPopup({ personaId, isOpen, onClose }) {
  const persona = getPersona(personaId);
  const tts = useSpeechSynthesis();
  const stt = useSpeechRecognition({ continuous: false, interim: true });
  const { autoSpeakEnabled, toggleAutoSpeak } = useAutoSpeakPreference();

  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const messagesEndRef = useRef(null);

  // Reset thread when persona changes
  useEffect(() => {
    if (!isOpen) return;
    setMessages([
      { id: 1, type: 'bot', text: persona.greeting, timestamp: new Date() },
    ]);
    setSessionId(`${persona.id}_${Date.now()}_${Math.random().toString(36).substr(2, 8)}`);
    setInput('');
    stt.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personaId, isOpen]);

  useEffect(() => {
    if (stt.transcript) setInput(stt.transcript);
  }, [stt.transcript]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Auto-speak the latest bot message (Architect Voice Mandate)
  useEffect(() => {
    if (!autoSpeakEnabled || !tts.supported || !isOpen) return;
    const last = messages[messages.length - 1];
    if (!last || last.type !== 'bot') return;
    tts.speak(last.text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, autoSpeakEnabled, isOpen]);

  const send = async () => {
    if (!input.trim() || loading) return;
    if (stt.listening) stt.stop();
    const userText = input.trim();
    setMessages((prev) => [
      ...prev,
      { id: prev.length + 1, type: 'user', text: userText, timestamp: new Date() },
    ]);
    setInput('');
    stt.reset();
    setLoading(true);

    try {
      const resp = await fetch(`${API_URL}/api/chat/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userText,
          session_id: sessionId,
          persona: persona.id,
        }),
      });
      const data = await resp.json();
      const text =
        (resp.ok && data?.response) ||
        `${persona.name} is reaching across the Federation. Try again in a moment.`;
      setMessages((prev) => [
        ...prev,
        { id: prev.length + 1, type: 'bot', text, timestamp: new Date() },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: prev.length + 1,
          type: 'bot',
          text: 'Connection error. The Memory Lattice may be syncing.',
          timestamp: new Date(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      data-testid="ai-chat-popup"
      data-persona={persona.id}
      style={{
        position: 'fixed',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 10001,
        width: '420px',
        maxWidth: 'calc(100vw - 32px)',
        height: '560px',
        maxHeight: 'calc(100vh - 120px)',
        background: 'linear-gradient(180deg, #0a0a0a 0%, #050b14 100%)',
        border: `1px solid ${persona.accent}66`,
        borderRadius: '16px',
        boxShadow: `0 0 60px rgba(0,0,0,0.7), 0 0 40px ${persona.accent}33`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        color: '#e5e7eb',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '14px 16px',
          background: `linear-gradient(135deg, ${persona.accent}aa 0%, ${persona.accent}66 100%)`,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <div
          style={{
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            overflow: 'hidden',
            border: '2px solid rgba(255,255,255,0.9)',
            flexShrink: 0,
            background: '#000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'ui-monospace, Menlo, monospace',
            fontWeight: 700,
            color: persona.accent,
          }}
        >
          <img
            src={persona.avatar}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '15px', fontWeight: 700, color: '#fff' }}>{persona.name}</div>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.9)' }}>{persona.role}</div>
        </div>
        <button
          onClick={() => { if (autoSpeakEnabled) tts.stop(); toggleAutoSpeak(); }}
          data-testid={`ai-chat-autospeak-${persona.id}`}
          aria-label={autoSpeakEnabled ? 'Mute auto voice' : 'Enable auto voice'}
          title={autoSpeakEnabled ? 'Auto-voice ON — click to mute' : 'Auto-voice OFF — click to enable'}
          style={{
            background: 'rgba(0,0,0,0.4)',
            color: '#fff',
            border: 'none',
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '6px',
          }}
        >
          {autoSpeakEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" style={{ opacity: 0.7 }} />}
        </button>
        <button
          onClick={onClose}
          data-testid="ai-chat-close"
          aria-label="Close chat"
          style={{
            background: 'rgba(0,0,0,0.4)',
            color: '#fff',
            border: 'none',
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {messages.map((m) => (
          <div key={m.id} style={{ display: 'flex', justifyContent: m.type === 'user' ? 'flex-end' : 'flex-start' }}>
            <div
              style={{
                maxWidth: '85%',
                padding: '10px 14px',
                borderRadius: '14px',
                background: m.type === 'user' ? persona.accent : 'rgba(255,255,255,0.06)',
                color: m.type === 'user' ? '#000' : '#e5e7eb',
                fontSize: '13px',
                lineHeight: 1.5,
              }}
            >
              <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{m.text}</p>
              {m.type === 'bot' && (
                <div style={{ marginTop: '6px', display: 'flex', justifyContent: 'flex-end' }}>
                  <SpeakButton
                    text={m.text}
                    supported={tts.supported}
                    speaking={tts.speaking}
                    onSpeak={tts.speak}
                    onStop={tts.stop}
                    testId={`speak-${persona.id}-${m.id}`}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <div style={{ padding: '10px 14px', borderRadius: '14px', background: 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Loader2 className="w-4 h-4 animate-spin" style={{ color: persona.accent }} />
              <span style={{ fontSize: '12px' }}>{persona.name} is reflecting...</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div style={{ padding: '12px', borderTop: `1px solid ${persona.accent}33`, display: 'flex', gap: '8px', alignItems: 'center', background: 'rgba(0,0,0,0.4)' }}>
        <MicButton
          supported={stt.supported}
          listening={stt.listening}
          onStart={stt.start}
          onStop={stt.stop}
          testId={`mic-${persona.id}`}
        />
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={onKey}
          placeholder={stt.listening ? 'Listening…' : `Ask ${persona.name}…`}
          disabled={loading}
          data-testid={`ai-chat-input-${persona.id}`}
          style={{
            flex: 1,
            padding: '10px 14px',
            background: '#000',
            border: `1px solid ${persona.accent}40`,
            borderRadius: '10px',
            color: '#e5e7eb',
            fontSize: '13px',
            outline: 'none',
          }}
        />
        <button
          onClick={send}
          disabled={loading || !input.trim()}
          data-testid={`ai-chat-send-${persona.id}`}
          aria-label="Send"
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            border: 'none',
            background: input.trim() && !loading ? persona.accent : 'rgba(255,255,255,0.1)',
            color: input.trim() && !loading ? '#000' : '#9ca3af',
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
